<template>
  <div>
    <b-modal id="deliveryModal" ref="deliveryModal" size="lg" :title="title">
      <form @submit.prevent="submitFormData">
        <div v-if="visibleSection.length === 0 || visibleSection.includes('address1')" class="form-group">
          <label class="form-label">{{ $t('label.address1') }}</label>
          <div class="input-group bg-white shadow-inset-2">
            <div class="input-group-prepend">
              <span class="input-group-text bg-transparent border-right-0">
                <i class="fas fa-store"></i>
              </span>
            </div>
            <input
                   type="text"
                   class="form-control bg-transparent pl-3"
                   :placeholder="$t('label.address1')"
                   v-model="address1"
                   required="">
          </div>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('address2')" class="form-group">
          <label class="form-label">{{ $t('label.address2') }}</label>
          <div class="input-group bg-white shadow-inset-2">
            <div class="input-group-prepend">
              <span class="input-group-text bg-transparent border-right-0">
                <i class="fas fa-store"></i>
              </span>
            </div>
            <input
                   type="text"
                   class="form-control bg-transparent pl-3"
                   :placeholder="$t('label.address2')"
                   v-model="address2"
                   required="">
          </div>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('postal_code')" class="form-group">
          <label class="form-label">{{ $t('label.postal_code') }}</label>
          <div class="input-group bg-white shadow-inset-2">
            <div class="input-group-prepend">
              <span class="input-group-text bg-transparent border-right-0">
                <i class="fas fa-map-marker-alt"></i>
              </span>
            </div>
            <input
                   type="number"
                   class="form-control bg-transparent pl-3"
                   :placeholder="$t('label.postal_code')"
                   v-model="postalCode"
                   required="">
          </div>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('city')" class="form-group">
          <label class="form-label">{{ $t('label.city') }}</label>
          <multiselect
                       v-model="city"
                       label="city"
                       track-by="cityID"
                       :options="cities"
                       :searchable="true"
                       :show-labels="false"
                       openDirection="below">
            <template slot="option" slot-scope="{ option }">{{ option.city }}</template>
          </multiselect>
          <label v-if="addressError" class="error-validation">{{ addressError }}</label>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('deliveryDate')" class="form-group">
          <label class="form-label">{{ $t('label.delivery_date') }}</label>
          <div class="row">
            <div class="col">
              <date-range-picker
                                 class="w-100"
                                 ref="deliveryDate"
                                 v-model="deliveryDate"
                                 :opens="'right'"
                                 :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy HH:MM' }"
                                 :showDropdowns="true"
                                 :always-show-calendars="true"
                                 :single-date-picker="true"
                                 :ranges="ranges"
                                 :time-picker="true"
                                 :time-picker24-hour="true"
                                 :time-picker-increment="1"
                                 :min-date="minDeliveryDate"
                                 :append-to-body="true"
                                 @select="onDatePickerSelect('deliveryDate')">
              </date-range-picker>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-secondary" @click="clearDeliveryDate"><i class="fas fa-trash"></i></button>
            </div>
          </div>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('packageDate')" class="form-group">
          <label class="form-label">{{ $t('label.packaged_date') }}</label>
          <div class="row">
            <div class="col">
              <date-range-picker
                                 class="w-100"
                                 ref="packageDate"
                                 v-model="packageDate"
                                 :opens="'right'"
                                 :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy HH:MM' }"
                                 :showDropdowns="true"
                                 :always-show-calendars="true"
                                 :single-date-picker="true"
                                 :ranges="ranges"
                                 :time-picker="true"
                                 :time-picker24-hour="true"
                                 :time-picker-increment="1"
                                 :append-to-body="true"
                                 :min-date="minPackageDate"
                                 @select="onDatePickerSelect('packageDate')">
              </date-range-picker>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-secondary" @click="clearPackagedDate"><i class="fas fa-trash"></i></button>
            </div>
          </div>
        </div>
        <div v-if="visibleSection.length === 0 || visibleSection.includes('deliveredDate')" class="form-group">
          <label class="form-label">{{ $t('label.delivered_date') }}</label>
          <div class="row">
            <div class="col">
              <date-range-picker
                                 class="w-100"
                                 ref="deliveredDate"
                                 v-model="deliveredDate"
                                 :opens="'right'"
                                 :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy HH:MM' }"
                                 :showDropdowns="true"
                                 :always-show-calendars="true"
                                 :single-date-picker="true"
                                 :ranges="ranges"
                                 :time-picker="true"
                                 :time-picker24-hour="true"
                                 :time-picker-increment="1"
                                 :min-date="minDeliveredDate"
                                 :append-to-body="true"
                                 @select="onDatePickerSelect('deliveredDate')">
              </date-range-picker>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-secondary" @click="clearDeliveredDate"><i class="fas fa-trash"></i></button>
            </div>
          </div>
        </div>
      </form>
      <template #modal-footer="{ close }">
        <div class="d-flex w-100 justify-content-end">
          <div class="d-flex">
            <b-button @click="close()">{{ $t('button_link.cancel') }}</b-button>
            <b-button class="ml-1" variant="primary" @click="submitForm">{{ $t('button_link.save') }}</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DateRangePicker from 'vue2-daterange-picker'
import moment from 'moment'
import { insertLeadingZero } from './../../../../assets/js/dashboard/utilities.js'
import { handleSessionExpired } from './../../../../assets/js/dashboard/dashboard'

export default {
  name: 'deliveryForm',
  props: {
  },
  components: {
    DateRangePicker
  },
  data() {
    return {
      title: '',
      cities: [],
      saveable: false,

      ranges: { 'Current': [new Date(), new Date()] },
      visibleSection: [],

      addressError: '',

      salesID: '',
      address1: '',
      address2: '',
      postalCode: '',
      city: null,
      deliveryDate: {},
      packageDate: {},
      deliveredDate: {},

      minDeliveryDate: null,
      minPackageDate: null,
      minDeliveredDate: null,

      value: null,
    }
  },
  watch: {
    "$i18n.locale"(val) {
    },
  },
  methods: {
    setCity(cities) {
      this.cities = cities
    },
    setData(item, minimum) {
      this.title = this.$i18n.t('label.delivery') + ' #' + insertLeadingZero(item.receiptID)
      this.salesID = item.saleID
      this.visibleSection = []

      if (item['address']) {
        this.address1 = item['address']['line1'] || ''
        this.address2 = item['address']['line2'] || ''
        this.postalCode = item['address']['postalCode'] || ''
        this.city = this.cities.find(k => k['cityID'] === item['address']['cityID'])
      } else {
        this.address1 = ''
        this.address2 = ''
        this.postalCode = ''
        this.city = null
      }

      let temp = moment.unix(item['createDate']).format('YYYY-MM-DD HH:mm:ss')
      this.minDeliveryDate = temp
      this.minPackageDate = temp
      this.minDeliveredDate = temp

      if (item['deliveredDate']) {
        let temp = moment.unix(item['deliveredDate'])
        this.deliveredDate = { startDate: temp, endDate: temp }
      } else {
        this.deliveredDate = {}
      }

      if (item['packageDate']) {
        let temp = moment.unix(item['packageDate'])
        this.packageDate = { startDate: temp, endDate: temp }
        this.minDeliveredDate = temp.format('YYYY-MM-DD HH:mm:ss')

        if(minimum) {
          if(this.visibleSection.length === 0) {
            this.visibleSection.push('deliveredDate')
          }
        }
      } else {
        this.packageDate = {}
      }

      if (item['deliveryDate']) {
        let temp = moment.unix(item['deliveryDate'])
        this.deliveryDate = { startDate: temp, endDate: temp }

        if(minimum) {
          if(this.visibleSection.length === 0) {
            this.visibleSection.push('packageDate')
          }
        }
      } else {
        this.deliveryDate = {}
      }

      if(minimum) {
        if(this.visibleSection.length === 0) {
          this.visibleSection.push('deliveryDate')
        }
      }

      this.$refs['deliveryModal'].show()
    },

    clearDeliveryDate() {
      this.deliveryDate = { startDate: null, endDate: null }
    },
    clearPackagedDate() {
      this.packageDate = { startDate: null, endDate: null }
    },
    clearDeliveredDate() {
      this.deliveredDate = { startDate: null, endDate: null }
    },

    onPackagedDateChange() {
      if(this.packageDate && this.packageDate.startDate) {
        this.minDeliveredDate = this.packageDate.startDate.format('YYYY-MM-DD HH:mm:ss')
      }
    },
    onDatePickerSelect(key) {
      let temp = moment()
      this[key] = { startDate: temp, endDate: temp }
      this.$refs[key].togglePicker(false)
    },

    async submitForm() {
      this.addressError = ''
      this.updateAddress()
    },
    updateAddress() {
      if(!this.address1 && !this.address2 && !this.postalCode && !this.city) {
        this.updateDeliveryDates()
        return
      }

      if(!this.city || !this.city.cityID) {
        this.addressError = 'Please select city for delivery address'
        return
      }

      let url = this.$api.BUSINESS_SALES + "/" + this.salesID + '/delivery/address/update'

      let address = {
        line1: this.address1,
        line2: this.address2,
        postalCode: this.postalCode,
        cityID: this.city.cityID,
      }

      let $vm = this
      $vm.$axios({
        method: 'post',
        url: url,
        baseURL: this.$api.API_ENDPOINT_ORDER,
        headers: { token: $vm.$session.get('token') },
        data: address,
      })
        .then((response) => {
          if (response.status == 200) {
            $vm.updateDeliveryDates()
          }
        })
        .catch((err) => {
          var errData = err.response.data;
          if (errData.error == $vm.$api.INVALID_TOKEN) {
            handleSessionExpired();
          } else {
            $vm.$parent.$parent.$parent.setModal($vm, true, true, $vm.$i18n.t('message.error.system_error'));
          }
        })
    },
    updateDeliveryDates() {
      let url = this.$api.BUSINESS_SALES + "/" + this.salesID + '/delivery/update'

      let data = {
        deliveryDate: this.deliveryDate.startDate ? moment(this.deliveryDate.startDate).unix() : '',
        packageDate: this.packageDate.startDate ? moment(this.packageDate.startDate).unix() : '',
        deliveredDate: this.deliveredDate.startDate ? moment(this.deliveredDate.startDate).unix() : '',
      }

      let $vm = this
      $vm.$axios({
        method: 'post',
        url: url,
        baseURL: this.$api.API_ENDPOINT_ORDER,
        headers: { token: $vm.$session.get('token') },
        data: data,
      })
        .then((response) => {
          if (response.status == 200) {
            var action = $vm.$i18n.t('message.success.action.update')
            var message = $vm.$i18n.t('message.success.success', { 'action': action })
            $vm.$parent.$parent.$parent.setModal($vm, false, true, message);
          }
        })
        .catch((err) => {
          var errData = err.response.data;
          if (errData.error == $vm.$api.INVALID_TOKEN) {
            handleSessionExpired();
          } else {
            $vm.$parent.$parent.$parent.setModal($vm, true, true, $vm.$i18n.t('message.error.system_error'));
          }
        })
    },

    closeModal(isError) {
      if(!isError) {
        this.$refs['deliveryModal'].hide()
        this.$emit('onModalClose')
      }
    }
  },
  async mounted() {
    let $vm = this;
  }
}
</script>
<style scoped>
.show {
  display: block;
}

.nav-link,
.link {
  cursor: pointer;
}

.img-thumbnail {
  width: 20em;
}

.item-image {
  width: 8em;
  opacity: .5;
}

.error-validation {
  font-size: 0.6875rem;
  margin-top: 0.325rem;
  color: #fd3995;
}
</style>